/**
 * "Raða": an editor (the conductor has role editor) or admin sets the SHARED order of a setlist /
 * folder — everyone sees it. Opened from CollectionScreen and from the song page's list drawer.
 *
 * ↑/↓ (44 pt) plus a native "Færa í sæti" select per row (the iPad wheel picker); no touch drag, so
 * scrolling a long list never moves a song by accident. The draft is built on a FRESH copy of the
 * list fetched when the sheet opens ("Vista röð" stays off until it arrives, and while offline) and
 * saved with that copy's updatedAt: if someone else saved in between, the server refuses (409), the
 * latest order is shown and the editor reorders again — nothing of theirs is lost. A refusal is
 * judged from the latest copy first (collectionOrder afterConflict): when only the name / description
 * changed the order is saved again on it, and when the list already is the editor's order (an earlier
 * save whose answer was lost) it counts as saved.
 */
import { ArrowDown, ArrowUp, ChevronsUpDown, RotateCw, WifiOff } from 'lucide-react';
import { memo, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { CollectionDTO, CollectionEntryDTO } from '../../../shared/dto/library';
import { isNetworkFailure } from '../../api/client';
import { CollectionChangedError, fetchCollection, storeCollection, useCollection, useSetCollectionEntries, useSongs } from '../../api/library';
import { useToast } from '../../components/toast';
import { Button, ConfirmSheet, IconButton, Sheet } from '../../components/ui';
import { useT } from '../../i18n';
import { useOnline } from '../../lib/useOnline';
import { afterConflict, inListOrder, moveTo, sameEntries, sameOrder, toEntriesRequest } from './collectionOrder';
import { errorText } from './format';

type Control = 'up' | 'down' | 'select';

export function ReorderSheet({ collectionId, open, onClose }: { collectionId: string; open: boolean; onClose: () => void }) {
  // Mounted only while open: every opening starts from a fresh fetch and an untouched draft.
  return open ? <ReorderBody collectionId={collectionId} onClose={onClose} /> : null;
}

function ReorderBody({ collectionId, onClose }: { collectionId: string; onClose: () => void }) {
  const t = useT();
  const toast = useToast();
  const online = useOnline();
  const qc = useQueryClient();
  const colQ = useCollection(collectionId, { refetchOnMount: false }); // load() below fetches it
  const songsQ = useSongs();
  const save = useSetCollectionEntries(collectionId);

  /** The fetched list the draft is built on (null while loading); its updatedAt guards the save. */
  const [base, setBase] = useState<CollectionDTO | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  /** The new order; null = untouched. */
  const [order, setOrder] = useState<CollectionEntryDTO[] | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [announce, setAnnounce] = useState('');
  const [focusReq, setFocusReq] = useState<{ entryId: string; control: Control } | null>(null);

  const loadSeq = useRef(0);
  const alive = useRef(true);
  /** Fetches the latest copy as the base: resolves to it, or null (failed — shown with a retry — or superseded / closed). */
  const load = useCallback(async (): Promise<CollectionDTO | null> => {
    const n = ++loadSeq.current;
    setBase(null);
    setLoadError(null);
    try {
      const fresh = await fetchCollection(collectionId);
      if (n !== loadSeq.current) return null;
      storeCollection(qc, fresh); // the screen behind shows the latest too
      setBase(fresh);
      return fresh;
    } catch (e) {
      if (n === loadSeq.current) setLoadError(e);
      return null;
    }
  }, [collectionId, qc]);

  useEffect(() => {
    alive.current = true;
    void load();
    return () => {
      alive.current = false;
      loadSeq.current++; // ignore an answer that arrives after closing
    };
  }, [load]);

  // Opened offline (or the fetch failed on the network): fetch again once the connection is back.
  const wasOnline = useRef(online);
  useEffect(() => {
    const back = online && !wasOnline.current;
    wasOnline.current = online;
    if (back && isNetworkFailure(loadError)) void load();
  }, [online, loadError, load]);

  const baseOrder = useMemo(() => (base ? inListOrder(base.entries) : null), [base]);

  const latest = colQ.data;
  const cachedOrder = useMemo(() => (latest ? inListOrder(latest.entries) : []), [latest]);
  const entries = order ?? baseOrder ?? cachedOrder;
  const ready = base !== null;
  const dirty = order !== null && baseOrder !== null && !sameOrder(order, baseOrder);

  // A newer copy arrived meanwhile (polling, window focus, the editor screen): adopt it when nothing
  // was moved yet, or when its entries are the same (e.g. only the name changed — that moves updatedAt
  // too). Otherwise keep the draft; saving it then gets the 409 and the latest order.
  useEffect(() => {
    if (!base || !baseOrder || !latest || latest.updatedAt <= base.updatedAt) return;
    if (!dirty) {
      setBase(latest);
      setOrder(null);
    } else if (sameEntries(cachedOrder, baseOrder)) {
      setBase(latest);
    }
  }, [latest, cachedOrder, base, baseOrder, dirty]);

  const titles = useMemo(() => new Map((songsQ.data ?? []).map((s) => [s.id, s.title])), [songsQ.data]);
  const titleOf = useCallback((e: CollectionEntryDTO) => titles.get(e.songId) ?? t('library.song_missing'), [titles, t]);

  // Stable row callback (rows are memoised: a move re-renders only the rows whose number changed).
  const entriesRef = useRef(entries);
  useLayoutEffect(() => {
    entriesRef.current = entries;
  });
  const onMove = useCallback(
    (from: number, to: number, control: Control) => {
      const cur = entriesRef.current;
      const next = moveTo(cur, from, to);
      if (next === cur) return;
      const moved = cur[from]!;
      setOrder(next);
      setAnnounce(t('library.moved_to', { title: titleOf(moved), n: to + 1, count: next.length }));
      setFocusReq({ entryId: moved.entryId, control });
    },
    [t, titleOf],
  );

  // Focus follows the moved row (keyboard / VoiceOver): it stays on the control that was used, or
  // moves to the other arrow when that one became disabled at the top / bottom. After the select,
  // only a lost focus goes to the row itself (re-focusing a select could reopen the iPad picker).
  const listRef = useRef<HTMLOListElement>(null);
  useEffect(() => {
    if (!focusReq) return;
    const row = listRef.current?.querySelector<HTMLElement>(`[data-entry="${CSS.escape(focusReq.entryId)}"]`);
    if (!row) return;
    const active = document.activeElement;
    const lost = !active || active === document.body || row.contains(active);
    const ctl = (c: Control) => row.querySelector<HTMLElement>(`[data-ctl="${c}"]`);
    const usable = (el: HTMLElement | null) => (el && !(el as HTMLButtonElement).disabled ? el : null);
    const target =
      focusReq.control === 'select' ? (active === ctl('select') ? null : row) : (usable(ctl(focusReq.control)) ?? usable(ctl(focusReq.control === 'up' ? 'down' : 'up')) ?? row);
    if (lost && target && active !== target) target.focus();
    else row.scrollIntoView({ block: 'nearest' });
  }, [focusReq]);

  const requestClose = () => {
    if (save.isPending) return;
    if (dirty) setConfirmDiscard(true);
    else onClose();
  };

  /**
   * Saves `sent` (built on the entries `from`) on version `baseUpdatedAt`. Refused (409): decided on
   * the latest copy, fetched now (the rows stay disabled meanwhile) — saved already, saved again once
   * when only the name / description changed (`from` null on that retry), else the latest order shows.
   */
  const submit = (sent: CollectionEntryDTO[], from: CollectionEntryDTO[] | null, baseUpdatedAt: number) => {
    const saved = () => {
      toast.show(t('library.reorder_saved'), { tone: 'ok' });
      onClose();
    };
    save.mutate(toEntriesRequest(sent, baseUpdatedAt), {
      onSuccess: saved,
      onError: (e) => {
        if (!(e instanceof CollectionChangedError)) {
          toast.show(errorText(t, e), { tone: 'error' });
          return;
        }
        void load().then((fresh) => {
          if (!alive.current) return;
          const outcome = fresh ? afterConflict(inListOrder(fresh.entries), sent, from) : 'stale';
          if (outcome === 'saved') saved();
          else if (outcome === 'retry' && fresh) submit(sent, null, fresh.updatedAt);
          else {
            toast.show(t('library.reorder_stale'), { tone: 'error', ms: 7000 });
            setOrder(null);
            setAnnounce('');
          }
        });
      },
    });
  };

  const onSave = () => {
    if (!base || !baseOrder || !order || !dirty) return;
    submit(order, baseOrder, base.updatedAt);
  };

  const options = useMemo(
    () =>
      Array.from({ length: entries.length }, (_, k) => (
        <option key={k} value={k}>
          {k + 1}
        </option>
      )),
    [entries.length],
  );

  const name = base?.name ?? latest?.name ?? '';
  let status: ReactNode = null;
  if (!online) {
    status = (
      <p role="status" className="flex items-center gap-2 rounded-xl bg-amber-100 px-3 py-2 font-medium text-amber-950 dark:bg-amber-900/40 dark:text-amber-100">
        <WifiOff className="size-5 shrink-0" aria-hidden />
        {t('library.reorder_offline')}
      </p>
    );
  } else if (loadError) {
    status = (
      <div role="alert" className="flex flex-wrap items-center gap-3 rounded-xl bg-[var(--surface-2)] px-3 py-2">
        <span className="min-w-0 flex-1 font-medium text-[var(--danger-text)]">{errorText(t, loadError)}</span>
        <Button variant="secondary" icon={RotateCw} onClick={() => void load()}>
          {t('common.retry')}
        </Button>
      </div>
    );
  } else if (!ready) {
    status = (
      <p role="status" className="text-[var(--muted)]">
        {t('library.reorder_loading')}
      </p>
    );
  }

  return (
    <>
      <Sheet
        open
        onClose={requestClose}
        wide
        title={t('library.reorder_title', { name })}
        footer={
          <>
            <Button variant="secondary" block onClick={requestClose} disabled={save.isPending}>
              {t('library.reorder_cancel')}
            </Button>
            <Button block onClick={onSave} busy={save.isPending} disabled={!ready || !online || !dirty}>
              {t('library.reorder_save')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3 py-1">
          <p className="text-[var(--ink-2)]">{t('library.reorder_hint')}</p>
          {status}
          <p className="sr-only" aria-live="polite" aria-atomic="true">
            {announce}
          </p>
          {entries.length > 0 ? (
            <ol ref={listRef} className="divide-y divide-[var(--line)]">
              {entries.map((e, i) => (
                <ReorderRow
                  key={e.entryId}
                  entry={e}
                  index={i}
                  count={entries.length}
                  title={titleOf(e)}
                  disabled={!ready || save.isPending}
                  options={options}
                  onMove={onMove}
                />
              ))}
            </ol>
          ) : ready ? (
            <p className="py-4 text-center text-[var(--muted)]">{t('library.collection_empty')}</p>
          ) : null}
        </div>
      </Sheet>
      <ConfirmSheet
        open={confirmDiscard}
        title={t('library.reorder_discard')}
        confirmLabel={t('library.reorder_discard_yes')}
        cancelLabel={t('library.stay')}
        danger
        onClose={() => setConfirmDiscard(false)}
        onConfirm={() => {
          setConfirmDiscard(false);
          onClose();
        }}
      />
    </>
  );
}

const ReorderRow = memo(function ReorderRow({
  entry,
  index,
  count,
  title,
  disabled,
  options,
  onMove,
}: {
  entry: CollectionEntryDTO;
  index: number;
  count: number;
  title: string;
  disabled: boolean;
  options: ReactNode;
  onMove: (from: number, to: number, control: Control) => void;
}) {
  const t = useT();
  const titleId = useId();
  // Every position (1…N) only once this row's picker is touched: N selects of N options each would
  // mount N² nodes (a 300-song folder: 90 000 — a freeze on an older iPad). Discrete events, so the
  // full list is in before iOS shows its wheel; focus arms it for keyboard and VoiceOver users.
  const [armed, setArmed] = useState(false);
  const arm = () => {
    if (!armed) setArmed(true);
  };
  return (
    <li data-entry={entry.entryId} tabIndex={-1} className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl py-3 outline-offset-0">
      <span
        className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[var(--accent-soft)] text-[17px] font-bold text-[var(--accent)] tabular-nums"
        aria-label={t('library.position', { n: index + 1 })}
      >
        {index + 1}
      </span>
      <div className="min-w-0 flex-1 basis-40">
        <div id={titleId} className="text-[18px] leading-snug font-semibold break-words">
          {title}
        </div>
        {entry.note ? <div className="text-[15px] break-words text-[var(--ink-2)] italic">{entry.note}</div> : null}
      </div>
      <div className="ml-auto flex shrink-0 items-center gap-1">
        <IconButton
          data-ctl="up"
          icon={ArrowUp}
          label={t('library.move_up_named', { title })}
          disabled={disabled || index === 0}
          onClick={() => onMove(index, index - 1, 'up')}
          className="disabled:opacity-30"
        />
        <IconButton
          data-ctl="down"
          icon={ArrowDown}
          label={t('library.move_down_named', { title })}
          disabled={disabled || index === count - 1}
          onClick={() => onMove(index, index + 1, 'down')}
          className="disabled:opacity-30"
        />
        <span className="relative inline-flex">
          <select
            data-ctl="select"
            value={index}
            disabled={disabled || count < 2}
            onPointerDown={arm}
            onMouseDown={arm}
            onFocus={arm}
            onKeyDown={arm}
            onChange={(e) => onMove(index, Number(e.target.value), 'select')}
            aria-label={t('library.move_to')}
            aria-describedby={titleId}
            title={t('library.move_to')}
            className="min-h-11 min-w-[4.75rem] appearance-none rounded-xl border border-[var(--line)] bg-[var(--surface-2)] py-2 pr-9 pl-3 font-semibold text-[var(--ink)] tabular-nums focus:border-[var(--focus)] disabled:opacity-40"
          >
            {armed ? options : <option value={index}>{index + 1}</option>}
          </select>
          <ChevronsUpDown className="pointer-events-none absolute top-1/2 right-2.5 size-5 -translate-y-1/2 text-[var(--muted)]" aria-hidden />
        </span>
      </div>
    </li>
  );
});
